'use strict';
/* ==========================================================================
   levels/rocks.js — step "rocks" (orientation any): ROCK'S LONG TRIP (DESIGN.md §5.3).

   You are a grain of rock. Each MISSION (pool "rocks", content/bank.rocks.json, drawn by core/bank.js: 5 per attempt,
   seeded, resumable) says "turn <from> into <to>". The board: your rock (a specimen: IMG[kind] when the art exists,
   else a labelled paper stand-in per kind), your place (surface / deep) as a marker on a torn-paper strata column, the
   goal rock, a move counter against par, and the deep-time clock (RockCycle.timeOf labels only: orders of magnitude,
   never a precise number). Nine MOVE CARDS come from RockCycle.legal(state): an illegal card is greyed and, tapped,
   shows its one-line reason; a played card animates the change (melt glow, crumble, press, squeeze, lift, erupt).
   Undo and Restart mission. When a move has more than one outcome (the model returns `choices`, e.g. a weathered
   granite gives sand AND mud), a small follow-up asks which grain to follow.
   Reaching the goal → the item's CHECK question (why / explain, as in the template quiz) → the mission's stars:
   reach it at par = 2, within par + 2 = 1, else 0 (a "show me" hint caps them at 0), +1 for the check right first try.
   Missions tagged "breaks-order" then show the point of the game: "Textbook order: N moves. You: M moves."
   (RockCycle.textbookSolve / solve). 5 missions × 3 = 15 stars; the stamp for reaching draw.pass missions (3 of 5; designer 2026-09-27).
   Every number shown comes from core-local/rockcycle.js (par = BFS, textbook = BFS on the loop, the clock's label).

   Progress: Game.progress.bank.rocks = the attempt (core/bank.js) + `run` (mission, moves, results) so a reload
   mid-attempt resumes exactly; Game.progress.scores.rocks = {best, last, of: 15}; each check's first answer under
   Game.progress.answers['rocks/' + instance.key]. Bank.finish at the end; Game.complete('rocks') at the pass mark.
   Portrait phone (Dock.active): the stage shows the rock and the column large; the cards, the check question and
   every control are native dock buttons. Keys: 1–9 cards, 1–3 answers (and grains), Backspace undo, Enter next.

   QA hooks: sc.phase ('intro' | 'play' | 'grain' | 'reached' | 'check' | 'answered' | 'end'), sc.mission (the
   instance), sc.instances, sc.mi, sc.state (the RockCycle state), sc.moves, sc.solution (BFS path from the current
   state), sc.par, sc.reason {move, text}, sc.results, sc.stars, sc.seed, sc.attempt, sc.resumed, sc.textbook();
   sc.begin(), sc.play(move) → bool, sc.undo(), sc.restart(), sc.hint(), sc.grain(i), sc.answer(i), sc.next(),
   sc.collect(); Levels.rocks.cards() (card rects). Integration compat: sc.missions (starts the attempt), sc.done().
   ========================================================================== */
(function () {
  const STEP = 'rocks';
  const R = () => window.RockCycle;
  const pool = () => Bank.pool(STEP) || Bank.load(Game.content.bank_rocks || { pool: STEP, draw: { n: 0 }, items: [] });
  const MOVES = ['weather', 'deposit', 'cement', 'squeeze', 'melt', 'cool', 'uplift', 'bury', 'erupt'];
  const base = m => String(m).split(':')[0];
  const variant = m => String(m).split(':')[1] || '';
  const label = m => ((R() && R().LABEL[base(m)]) || base(m)) + (variant(m) ? ' (' + variant(m) + ')' : '');
  const REACH_HOLD = 1.3, ANIM = 0.9;

  /* ---------------- look ---------------- */
  const CARD = {
    weather: { c: PAL.sky, ink: true, sub: 'rain, ice, wind' },     deposit: { c: PAL.kraft, ink: true, sub: 'pile up in layers' },
    cement: { c: PAL.mustard, ink: true, sub: 'glue grains to rock' }, squeeze: { c: PAL.lilac, ink: true, sub: 'heat + pressure' },
    melt: { c: PAL.red, ink: false, sub: 'into magma' },               cool: { c: PAL.teal, ink: false, sub: 'magma to rock' },
    uplift: { c: PAL.green, ink: false, sub: 'up to the surface' },    bury: { c: PAL.navy, ink: false, sub: 'push it deep' },
    erupt: { c: PAL.tomato, ink: false, sub: 'magma out' },
  };
  const GREY = '#b3aa9c';
  const ROCK = {
    granite: '#c9b8a8', rhyolite: '#d7b2a2', basalt: '#4a4a52', gabbro: '#3b4640', sandstone: '#d8b98a', shale: '#8f8a7e',
    limestone: '#e8e0c8', quartzite: '#efe9df', slate: '#5b6168', marble: '#f3efe7', gneiss: '#a39a8f', magma: '#e0533d',
    sand: '#e3c78f', mud: '#8a6a4a', shells: '#f1e6cf',
  };
  const FAM = { magma: 'melted rock', igneous: 'igneous rock', sediment: 'loose sediment', sedimentary: 'sedimentary rock', metamorphic: 'metamorphic rock' };
  // student-facing: where each magma comes from (facts-ground.md §11 summary #3 wording; the model's MAGMA_FROM wins)
  const MAGMA_FROM = { granitic: 'from melted continental rock', basaltic: 'from deep ocean-floor and mantle rock' };
  const magmaFrom = c => (R() && R().MAGMA_FROM && R().MAGMA_FROM[c] ? 'from ' + R().MAGMA_FROM[c] : MAGMA_FROM[c] || '');
  const kindSeed = k => 11 + Object.keys(ROCK).indexOf(k) * 7;
  const nameOf = s => (!s ? '' : s.kind !== 'magma' ? s.kind : (s.comp === 'basaltic' ? 'basalt-type ' : 'granite-type ') + (s.place === 'surface' ? 'lava' : 'magma'));
  const subOf = s => {
    if (!s) return '';
    const f = R().family(s);
    if (s.kind === 'magma') return magmaFrom(s.comp);
    if (f === 'igneous') return ['granite', 'gabbro'].includes(s.kind) ? R().CRYSTALS.deep : R().CRYSTALS.surface;
    return FAM[f] || f;
  };
  const cap = s => s.charAt(0).toUpperCase() + s.slice(1);

  /* the specimen: the engraving cut-out when it exists, else a paper stand-in with the kind's marks (cached sprite) */
  const shape = (k, r) => blobPts(r, Math.round(r * (k === 'sand' || k === 'mud' ? 0.62 : 0.74)), kindSeed(k), k === 'magma' ? 0.1 : k === 'sand' || k === 'mud' ? 0.06 : 0.16, 36);
  const markSlots = new Map();
  function marks(k, r) {
    const q = rng(kindSeed(k) * 131 + r), rx = r * 0.9, ry = r * 0.7;
    const at = () => { for (;;) { const x = (q() * 2 - 1) * rx, y = (q() * 2 - 1) * ry; if ((x * x) / (rx * rx) + (y * y) / (ry * ry) <= 1) return [x, y]; } };
    const dot = (c, s) => { const [x, y] = at(); g.fillStyle = c; g.beginPath(); g.arc(x, y, s, 0, TAU); g.fill(); };
    const wave = (y, amp, c, w, ph) => { g.strokeStyle = c; g.lineWidth = w; g.beginPath(); for (let x = -r; x <= r; x += 8) { const yy = y + Math.sin(x / r * 3 + ph) * amp; if (x === -r) g.moveTo(x, yy); else g.lineTo(x, yy); } g.stroke(); };
    const shell = (c) => { const [x, y] = at(), s = r * (0.1 + q() * 0.07), a = q() * TAU; g.save(); g.translate(x, y); g.rotate(a); g.strokeStyle = c; g.lineWidth = 2.5; g.beginPath(); g.arc(0, 0, s, Math.PI, TAU); g.closePath(); g.stroke(); for (let i = 1; i < 4; i++) { g.beginPath(); g.moveTo(0, 0); g.lineTo(Math.cos(Math.PI + i * Math.PI / 4) * s, Math.sin(Math.PI + i * Math.PI / 4) * s); g.stroke(); } g.restore(); };
    g.lineCap = 'round';
    switch (k) {
      case 'granite': for (let i = 0; i < 110; i++) dot(['#2b2420', '#f4ece2', '#e6a79a', '#fffdf7'][i % 4], r * (0.015 + q() * 0.035)); break;
      case 'rhyolite': for (let i = 0; i < 4; i++) wave(-ry * 0.6 + i * ry * 0.4, 6, 'rgba(150,80,65,0.45)', 3, i); for (let i = 0; i < 140; i++) dot(i % 3 ? 'rgba(255,255,255,0.7)' : '#9c5a4c', r * 0.012); break;
      case 'basalt': for (let i = 0; i < 70; i++) dot(i % 5 ? '#2a2a30' : '#7a7a82', r * (0.01 + q() * 0.03)); break;
      case 'gabbro': for (let i = 0; i < 46; i++) { const [x, y] = at(), s = r * (0.05 + q() * 0.06); g.save(); g.translate(x, y); g.rotate(q() * 3); g.fillStyle = i % 2 ? '#1c211e' : '#6f7c74'; g.fillRect(-s / 2, -s / 3, s, s * 0.66); g.restore(); } break;
      case 'sandstone': for (let i = 0; i < 5; i++) wave(-ry * 0.8 + i * ry * 0.4, 4, 'rgba(150,110,60,0.55)', 4, i * 0.7); for (let i = 0; i < 90; i++) dot('rgba(120,90,50,0.5)', r * 0.012); break;
      case 'shale': for (let i = 0; i < 11; i++) wave(-ry + i * ry * 0.2, 1.5, 'rgba(60,56,50,0.55)', 2.5, i); break;
      case 'limestone': for (let i = 0; i < 8; i++) shell('rgba(150,135,100,0.8)'); break;
      case 'quartzite': for (let i = 0; i < 90; i++) dot(i % 2 ? '#fffdf7' : 'rgba(170,160,140,0.6)', r * (0.012 + q() * 0.02)); break;
      case 'slate': g.strokeStyle = 'rgba(30,34,40,0.6)'; g.lineWidth = 3; for (let i = -6; i <= 6; i++) { g.beginPath(); g.moveTo(i * r * 0.18 - r * 0.3, -r); g.lineTo(i * r * 0.18 + r * 0.3, r); g.stroke(); } break;
      case 'marble': for (let i = 0; i < 4; i++) wave(-ry * 0.5 + i * ry * 0.35, 18, 'rgba(120,118,112,0.5)', 2.5, i * 1.9); break;
      case 'gneiss': for (let i = 0; i < 7; i++) wave(-ry + i * ry * 0.33, 10, i % 2 ? 'rgba(60,54,48,0.75)' : 'rgba(245,238,228,0.9)', r * 0.1, i * 0.4); break;
      case 'magma': g.strokeStyle = '#f7c948'; g.lineWidth = 5; for (let i = 0; i < 6; i++) { let [x, y] = at(); g.beginPath(); g.moveTo(x, y); for (let j = 0; j < 4; j++) { x += (q() - 0.5) * r * 0.5; y += (q() - 0.5) * r * 0.35; g.lineTo(x, y); } g.stroke(); } for (let i = 0; i < 12; i++) dot('rgba(251,231,161,0.8)', r * 0.03); break;
      case 'sand': for (let i = 0; i < 160; i++) dot(i % 3 ? 'rgba(160,120,60,0.6)' : '#fffdf7', r * (0.012 + q() * 0.015)); break;
      case 'mud': for (let i = 0; i < 5; i++) dot('rgba(70,50,32,0.45)', r * (0.08 + q() * 0.1)); for (let i = 0; i < 6; i++) dot('rgba(255,255,255,0.25)', r * 0.03); break;
      case 'shells': for (let i = 0; i < 9; i++) shell('rgba(140,120,90,0.9)'); break;
      default: break;
    }
  }
  function specimen(s, x, y, r, { sc = 1, sx = 1, sy = 1, alpha = 1, rot = 0, depth = 1.3 } = {}) {
    if (!s || sc <= 0.001 || alpha <= 0) return;
    const k = s.kind;
    if (IMG[k]) { cut(k, x, y, r * 1.75, { s: sc, sx, sy, alpha, rot, depth, boil: false, seed: kindSeed(k) }); return; }
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(sc * sx, sc * sy); g.globalAlpha *= alpha;
    const pts = shape(k, r);
    // a scissor-cut paper border (the same shape, scaled) + an ink edge, so the code-drawn kinds (granite, rhyolite,
    // gabbro, shale, quartzite, magma) sit with the engraving cut-outs, which have a paper border of their own
    const bs = 1 + 14 / r;
    g.save(); g.scale(bs, bs); paperPiece(pts, 0, 0, '#fbf5e6', { seed: kindSeed(k) + 1, depth: depth / bs, boil: false }); g.restore();
    paperPiece(pts, 0, 0, ROCK[k] || PAL.kraft, { seed: kindSeed(k), depth: 0, boil: false });
    const body = () => { g.save(); pathPts(pts); g.clip(); marks(k, r); pathPts(pts); g.strokeStyle = 'rgba(43,36,32,0.6)'; g.lineWidth = 5; g.stroke(); g.restore(); };
    let slot = markSlots.get(k + '|' + r); if (!slot) markSlots.set(k + '|' + r, slot = {});
    if (!(FX.sprites && _ctm() && spriteIn(slot, -r * 1.25, -r * 1.25, r * 2.5, r * 2.5, body))) body();
    g.restore();
  }

  /* the strata column (static torn strips: cached sprites) and the grain's marker on it */
  function column(cx, top, w, h, big) {
    const sky = h * 0.14, ground = h * 0.06;
    paperRect(cx, top + sky / 2, w, sky, PAL.sky, { seed: 501, torn: true, depth: 1, boil: false });
    paperRect(cx, top + sky + ground / 2, w, ground + 10, '#d8b98a', { seed: 502, torn: true, depth: 0.8, boil: false });
    const L = [['#e8e0c8', 1], ['#d8b98a', 0.9], ['#8f8a7e', 0.8], ['#c9b8a8', 1], ['#4a4a52', 0.8], ['#d9763a', 1.3]];
    const tot = L.reduce((a, l) => a + l[1], 0), deepH = h - sky - ground;
    let y = top + sky + ground;
    L.forEach(([c, f], i) => { const lh = deepH * f / tot; paperRect(cx, y + lh / 2 + 4, w, lh + 12, c, { seed: 510 + i, torn: true, depth: 0.6, boil: false, rot: i % 2 ? 0.01 : -0.008 }); y += lh; });
    const fs = big ? 58 : 34;
    hand('SURFACE', cx, top + sky * (big ? 0.2 : 0.28), fs, -1e6, { dur: 0.01, color: PAL.navy, seed: 520 });
    paperRect(cx, top + h * 0.6, w * 0.78, fs * 1.4, PAL.cream, { seed: 521, depth: 0.8, boil: false, rot: -0.02 });
    hand('DEEP DOWN', cx, top + h * 0.6, fs, -1e6, { dur: 0.01, color: PAL.ink, seed: 522 });
    return { surf: top + sky + ground * 0.2, deep: top + h * 0.82 };
  }

  /* ---------------- the scene ---------------- */
  class Rocks extends Scene {
    get pool() { return pool(); }
    get n() { return this.instances ? this.instances.length : this.pool.draw.n; }
    get of() { return this.n * 3; }
    get passMissions() { return Math.min(this.n, this.pool.draw.pass || this.n); }
    get reachedCount() { return (this.results || []).filter(r => r && r.moves > 0).length; }
    get mission() { return this.instances ? this.instances[this.mi] : null; }
    get goal() { const m = this.mission; return m ? m.item.mission.to : null; }
    get start() { const m = this.mission && this.mission.item.mission; return m ? R().asState(m.from, m.place, m.comp) : null; }
    get state() { return this.states ? this.states[this.states.length - 1] : null; }
    get par() { return this.parInfo ? this.parInfo.par : -1; }
    get solution() { const s = this.state; if (!s || this.goal == null) return []; const r = R().solve(s, this.goal); return r ? r.path : []; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get reached() { return !!this.state && this.goal != null && this.state.kind === this.goal; }
    /* integration compat (the placeholder's hooks): the drawn missions (starting the attempt), and "stamp now" */
    get missions() { if (!this.instances) this.begin(); return this.instances; }

    enter() {
      this.phase = 'intro'; this.instances = null; this.mi = 0; this.results = []; this.passed = false; this.collected = false; this.reason = null; this.anim = null;
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();                  // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); Audio.stopGroup('rocks'); }

    /* ---- attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || {}, prev = S[STEP] && S[STEP].open ? S[STEP].run : null;
      const a = Bank.attempt(STEP, this.pool, { fresh });
      this.instances = a.instances; this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false;
      if (a.resumed && prev && this.restore(prev)) return;
      this.startMission(0);
    }
    restore(run) {
      try {
        if (!(run.m >= 0 && run.m < this.instances.length)) return false;
        this.mi = run.m; this.results = (run.res || []).slice(0, run.m);
        this.setup(); this.hinted = !!run.hinted;
        this.states = R().run(this.start, run.moves || []); this.moves = (run.moves || []).slice();
        this.pick = run.pick != null ? run.pick : -1;
        this.phase = !this.reached ? 'play' : this.pick >= 0 ? 'answered' : 'check';
        if (this.phase === 'answered') this.results[this.mi] = run.res && run.res[this.mi] || this.score(this.pick);
        this.qAt = this.pickedAt = Game.time - 1; this.phaseAt = Game.time;
        this.save();
        return true;
      } catch (e) { console.warn('[rocks] could not resume: ' + e.message); return false; }
    }
    setup() {
      this.parInfo = R().solve(this.start, this.goal);
      this.tb = R().textbookSolve(this.start, this.goal);
      this.reason = null; this.anim = null; this.pending = null; this.hintAt = null;
    }
    startMission(i) {
      this.mi = i; this.setup();
      this.states = [this.start]; this.moves = []; this.hinted = false; this.pick = -1;
      this.phase = 'play'; this.phaseAt = Game.time;
      this.save();
      Audio.play('paper', { gain: 0.45, rate: 1.05 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open) { S.run = { m: this.mi, moves: this.moves.slice(), hinted: !!this.hinted, pick: this.pick, res: this.results.slice() }; }
      Game.progress.save();
    }

    /* ---- moves ---- */
    legal() { return this.state ? R().legal(this.state) : []; }
    play(move) {
      if (this.phase === 'grain' && this.pending && base(move) === this.pending.move && variant(move)) { this.phase = 'play'; this.pending = null; }
      if (this.phase !== 'play' || !this.state) return false;
      if (!move) return false;
      const s = this.state, r = R().tryMove(s, move);
      if (!r.ok) {
        this.reason = { move: base(move), text: r.reason, at: Game.time };
        Audio.play('thunk', { gain: 0.5, rate: 0.8 });
        return false;
      }
      if (r.choices && r.choices.length > 1 && !variant(move)) {                   // more than one outcome: which grain?
        this.pending = { move: base(move), choices: r.choices, at: Game.time };
        this.phase = 'grain'; this.reason = null;
        Audio.play('paper', { gain: 0.5 });
        return true;
      }
      this.reason = null;
      this.states.push(r.to); this.moves.push(r.move || move);
      this.anim = { move: base(move), at: Game.time, from: s, to: r.to };
      { const [nm, gn, rt, dur] = SFX[base(move)]; Audio.schedule(nm, { gain: gn, rate: rt, dur: dur || null, group: 'rocks' }); }
      if (this.reached) { this.phase = 'reached'; this.phaseAt = Game.time; Audio.play('sparkle', { gain: 0.7 }); }
      this.save();
      return true;
    }
    grain(i) {
      if (this.phase !== 'grain' || !this.pending) return false;
      const c = this.pending.choices[i]; if (!c) return false;
      const mv = c.move || this.pending.move + ':' + (c.to && c.to.kind);
      this.phase = 'play'; this.pending = null;
      return this.play(mv);
    }
    cancelGrain() { if (this.phase === 'grain') { this.phase = 'play'; this.pending = null; } }
    undo() {
      this.cancelGrain();
      if (this.phase !== 'play' || !this.moves.length) return false;
      this.moves.pop(); this.states.pop(); this.anim = null; this.reason = null;
      Audio.play('scribble', { gain: 0.35, rate: 1.2 });
      this.save();
      return true;
    }
    restart() {
      this.cancelGrain();
      if (this.phase !== 'play' || !this.moves.length) return false;
      this.states = [this.start]; this.moves = []; this.anim = null; this.reason = null;
      Audio.play('tear', { gain: 0.45 });
      this.save();
      return true;
    }
    get canHint() { return this.phase === 'play' && this.par >= 0 && this.moves.length > this.par + 2; }
    hint() {
      if (!this.canHint && !(this.phase === 'play' && this.hinted)) return null;
      this.hinted = true; this.hintAt = Game.time; this.save();
      return this.solution[0] || null;
    }

    /* ---- check question ---- */
    score(pick) {
      const n = this.moves.length, p = this.par, inst = this.mission;
      const moveStars = this.hinted ? 0 : n === p ? 2 : n <= p + 2 ? 1 : 0;
      const right = pick === inst.answerIndex;
      return { id: inst.id, key: inst.key, moves: n, par: p, hinted: !!this.hinted, moveStars, check: right, stars: moveStars + (right ? 1 : 0),
        textbook: inst.tags.includes('breaks-order') ? (this.tb ? this.tb.par : null) : undefined };
    }
    answer(i) {
      const inst = this.mission;
      if (this.phase !== 'check' || !inst || i < 0 || i >= inst.options.length) return false;
      this.pick = i; this.pickedAt = Game.time; this.phase = 'answered';
      const res = this.score(i);
      this.results[this.mi] = res;
      Game.progress.answer(STEP + '/' + inst.key, inst.options[i]);                 // first try only
      Audio.play(res.check ? 'ding' : 'wahwah', { gain: res.check ? 0.8 : 0.3 });
      this.save();
      return true;
    }
    /* the point of the game: the textbook loop vs your route (null unless the mission is tagged breaks-order) */
    textbook() {
      const inst = this.mission;
      if (!inst || !inst.tags.includes('breaks-order')) return null;
      return { textbook: this.tb ? this.tb.par : null, you: this.moves.length, par: this.par };
    }
    next() {
      if (this.phase !== 'answered' || Game.time - this.pickedAt < 0.3) return false;
      if (this.mi < this.instances.length - 1) this.startMission(this.mi + 1);
      else this.finish();
      return true;
    }
    finish() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = this.reachedCount >= this.passMissions;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Bank.finish(STEP);                                                            // the next attempt draws new missions
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    /* a pass that leaves without "Collect your stamp" still keeps its stamp */
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only (tools/integration.js): end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.instances) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', color: PAL.red, go: () => this.collect() }, { label: 'New missions', color: PAL.teal, go: () => this.begin() }]
        : [{ label: 'New missions', color: PAL.red, go: () => this.begin() }, { label: 'Back to the map', color: PAL.teal, go: () => Game.replace(new Levels.map()) }];
    }

    update() {
      if (this.phase === 'reached' && Game.time - this.phaseAt > REACH_HOLD) { this.phase = 'check'; this.qAt = Game.time; Audio.play('paper', { gain: 0.5 }); }
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'play') { if (n >= 1 && n <= 9) this.play(MOVES[n - 1]); else if (k === 'Backspace') this.undo(); }
      else if (this.phase === 'grain') { if (n >= 1 && n <= this.pending.choices.length) this.grain(n - 1); else if (k === 'Backspace') this.cancelGrain(); }
      else if (this.phase === 'check') { if (n >= 1 && n <= 3) this.answer(n - 1); }
      else if (this.phase === 'answered') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    draw() {
      const docked = UI.docked();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      const checking = this.phase === 'check' || this.phase === 'answered';
      const sub = this.instances && this.phase !== 'end' && !(checking && !docked) ? `mission ${this.mi + 1} of ${this.n}` + (docked && this.par >= 0 ? ` · moves ${this.moves.length} · par ${this.par}` : '') : '';
      UI.hud("ROCK'S LONG TRIP", sub);
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      if (docked) this.drawBoardDocked(); else if (!checking) this.drawBoard();
      if (this.phase === 'grain') this.drawGrain(docked);
      if (this.phase === 'reached') UI.stamp('REACHED!', docked ? 1250 : 790, docked ? 520 : 450, { rot: -0.12, s: pop(T, this.phaseAt + 0.15, 0.3) * 1.3, color: PAL.green });
      if (this.phase === 'check' || this.phase === 'answered') this.drawCheck(docked);
    }

    /* the grain's marker position on the column (animated between places) and the specimen's animation */
    markerY(C) {
      const A = this.anim, yOf = s => (s.place === 'surface' ? C.surf : C.deep);
      if (!A || T - A.at >= ANIM) return yOf(this.state);
      return lerp(yOf(A.from), yOf(A.to), E.outCubic(P(T, A.at, A.at + ANIM * 0.85)));
    }
    drawRock(x, y, r) {
      const A = this.anim, s = this.state;
      if (!A || T - A.at >= ANIM) { specimen(s, x, y, r); return; }
      const age = T - A.at, p1 = P(age, 0, ANIM * 0.5), inP = pop(T, A.at + ANIM * 0.45, 0.4, 2.2);
      const m = A.move, first = age < ANIM * 0.5;
      if (m === 'melt' || m === 'erupt') paperCircle(x, y, r * 1.25, '#f08a3c', { seed: 601, s: 0.3 + 0.9 * E.outCubic(P(age, 0, ANIM * 0.6)), alpha: 0.55 * (1 - P(age, ANIM * 0.6, ANIM)), depth: 0, boil: false });
      if (m === 'cool') paperCircle(x, y, r * 1.2, PAL.sky, { seed: 602, s: 1.2 - 0.4 * P(age, 0, ANIM), alpha: 0.5 * (1 - P(age, ANIM * 0.5, ANIM)), depth: 0, boil: false });
      if (first) {
        let o = {};
        if (m === 'weather') o = { sc: 1 - 0.35 * p1, rot: Math.sin(age * 60) * 0.03 * (1 - p1) };
        else if (m === 'squeeze') o = { sx: 1 - 0.28 * p1, sy: 1 + 0.08 * p1 };
        else if (m === 'cement' || m === 'deposit' || m === 'bury') o = { sy: 1 - 0.22 * p1, sx: 1 + 0.06 * p1 };
        else if (m === 'melt') o = { sy: 1 - 0.35 * p1, alpha: 1 - 0.6 * p1 };
        else if (m === 'uplift' || m === 'erupt') o = { sc: 1 + 0.08 * Math.sin(p1 * Math.PI) };
        specimen(A.from, x, y, r, o);
      } else specimen(s, x, y, r, { sc: inP });
      // effects over the rock
      if (m === 'weather') for (let i = 0; i < 8; i++) {
        const a = i / 8 * TAU + 0.3, d = r * (0.6 + 1.4 * E.outCubic(P(age, 0.05, ANIM * 0.8)));
        paperPiece(blobPts(16, 12, 610 + i, 0.2, 10), x + Math.cos(a) * d, y + Math.sin(a) * d * 0.7 + 60 * P(age, 0.3, ANIM), ROCK[A.from.kind] || PAL.kraft, { seed: 610 + i, depth: 0.5, alpha: 1 - P(age, ANIM * 0.6, ANIM), boil: false, rot: a });
      }
      if (m === 'squeeze') {
        const push = 50 * E.outCubic(P(age, 0, ANIM * 0.5)), al = 1 - P(age, ANIM * 0.7, ANIM);
        paperArrow(x - r - 150 + push, y, 130, 46, PAL.lilac, { rot: 0, seed: 620, depth: 0.8 * al, s: al > 0 ? 1 : 0 });
        paperArrow(x + r + 150 - push, y, 130, 46, PAL.lilac, { rot: Math.PI, seed: 621, depth: 0.8 * al, s: al > 0 ? 1 : 0 });
      }
      if (m === 'cement' || m === 'deposit' || m === 'bury') for (let i = 0; i < 3; i++) {
        const drop = E.outCubic(P(age, i * 0.08, ANIM * 0.5 + i * 0.08)), al = 1 - P(age, ANIM * 0.7, ANIM);
        if (al > 0) paperRect(x, y - r * 0.9 - 130 * (1 - drop) - i * 30, r * 2.3, 32, ['#d8b98a', '#8f8a7e', '#e8e0c8'][i], { seed: 630 + i, torn: true, depth: 0.6, alpha: al, boil: false });
      }
      if (m === 'erupt') burst(x, y - r * 0.4, r * 1.3, T, A.at, { color: PAL.mustard, seed: 640, n: 10 });
      if (m === 'uplift') inkArrow(x + r + 60, y + 60 - 80 * P(age, 0, ANIM), x + r + 60, y - 120 - 80 * P(age, 0, ANIM), { w: 7, color: PAL.green, seed: 650, prog: P(age, 0, 0.3) });
    }
    drawMarker(C, cx, rad) {
      const y = this.markerY(C);
      paperCircle(cx, y, rad * 1.18, PAL.white, { seed: 660, depth: 1.2, boil: false });
      specimen(this.state, cx, y, rad, { depth: 0 });
      return y;
    }
    /* the state the specimen shows right now (the old one for the first half of a move's animation) */
    shown() { const A = this.anim; return A && T - A.at < ANIM * 0.5 ? A.from : this.state; }
    clockLabel() { try { return R().timeOf(this.start, this.moves).label; } catch (e) { return ''; } }
    drawClock(x, y, r, lines, size, w) {
      paperCircle(x, y, r, PAL.cream, { seed: 670, depth: 0.9, boil: false });
      g.save(); g.strokeStyle = PAL.ink; g.lineCap = 'round';
      const spin = this.moves.length * 1.7 + (this.anim ? 6 * E.outCubic(P(T, this.anim.at, this.anim.at + ANIM)) : 0);
      g.lineWidth = r * 0.12; g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(spin * 0.25 - 1.6) * r * 0.5, y + Math.sin(spin * 0.25 - 1.6) * r * 0.5); g.stroke();
      g.lineWidth = r * 0.08; g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(spin - 1.6) * r * 0.75, y + Math.sin(spin - 1.6) * r * 0.75); g.stroke();
      g.restore();
      const L = UI._wrap(lines, w, `${size}px 'Caveat Brush'`);
      L.forEach((l, i) => hand(l, x + r + 18, y - (L.length - 1) * size * 0.55 + i * size * 1.1, size, -1e6, { dur: 0.01, align: 'left', color: PAL.navy, seed: 671 + i }));
    }

    /* landscape / desktop board */
    drawBoard() {
      const inst = this.mission, m = inst.item.mission;
      const C = column(250, 200, 300, 850, false);
      this.drawMarker(C, 250, 46);
      hand('you', 250 + 100, this.markerY(C), 36, -1e6, { dur: 0.01, color: PAL.red, seed: 680 });
      // mission banner
      paperRect(1090, 206, 1020, 88, PAL.white, { seed: 700 + this.mi, depth: 1.1, rot: -0.006, boil: false });
      hand(`Turn ${m.from} into ${m.to}.`, 1090, 208, 54, this.phaseAt, { color: PAL.ink, seed: 701, dur: 0.4 });
      // your rock → the goal
      this.drawRock(790, 450, 140);
      const s = this.shown();
      hand(nameOf(s), 790, 648, 60, -1e6, { dur: 0.01, color: PAL.ink, seed: 702 });
      hand(subOf(s) + (s.kind === 'magma' || R().family(s) === 'igneous' ? '' : ' · ' + s.place), 790, 700, 34, -1e6, { dur: 0.01, color: PAL.navy, seed: 703 });
      inkArrow(990, 450, 1110, 450, { w: 6, color: PAL.navy, seed: 704, head: 30 });
      hand('GOAL', 1250, 318, 40, -1e6, { dur: 0.01, color: PAL.red, seed: 705 });
      specimen({ kind: m.to, place: 'surface', comp: 'granitic' }, 1250, 450, 92, { depth: 1 });
      hand(m.to, 1250, 578, 46, -1e6, { dur: 0.01, color: PAL.ink, seed: 706 });
      // the index card: moves vs par, the deep-time clock, the route so far
      paperRect(1690, 440, 350, 420, PAL.cream, { seed: 710, depth: 1.3, rot: 0.012, boil: false });
      tape(1690, 236, 120, -0.05, undefined, 711);
      hand(`moves ${this.moves.length}`, 1690, 290, 58, -1e6, { dur: 0.01, color: this.par >= 0 && this.moves.length > this.par + 2 ? PAL.red : PAL.ink, seed: 712 });
      hand(`par ${this.par}`, 1690, 346, 42, -1e6, { dur: 0.01, color: PAL.navy, seed: 713 });
      this.drawClock(1560, 430, 34, 'Time so far: ' + (this.moves.length ? this.clockLabel() : 'none yet'), 28, 250);
      const trail = this.moves.map(label).join(', ');
      if (trail) UI._wrap(trail, 310, "28px 'Caveat Brush'").slice(-4).forEach((l, i) => hand(l, 1690, 520 + i * 32, 28, -1e6, { dur: 0.01, color: PAL.ink, seed: 714 + i }));
      else hand('your route shows here', 1690, 540, 28, -1e6, { dur: 0.01, color: 'rgba(43,36,32,0.5)', seed: 714 });
      // undo / restart / hint
      const play = this.phase === 'play';
      if (play) {
        if (UI.button('undo', 'Undo', 1570, 725, { w: 170, h: 74, color: PAL.navy })) this.undo();
        if (UI.button('restart', '↺ Restart', 1800, 725, { w: 190, h: 74, color: PAL.teal })) this.restart();
        if (UI.keys) hand('Backspace = undo', 1690, 622, 24, -1e6, { dur: 0.01, color: PAL.navy, seed: 719 });
        if (this.canHint || (this.hinted && this.phase === 'play')) {
          if (UI.button('hint', 'Stuck? Show me', 1250, 700, { w: 300, h: 70, color: PAL.mustard, text: PAL.ink })) this.hint();
          if (this.hinted) { paperRect(1250, 768, 330, 46, PAL.white, { seed: 717, depth: 0.6, rot: 0.01, boil: false }); hand('hint used: no move stars', 1250, 768, 28, -1e6, { dur: 0.01, color: PAL.red, seed: 718 }); }
        }
      }
      this.drawCards(play);
      if (this.reason && play && T - this.reason.at < 4) this.drawReason();
    }
    cardRects() {
      const w = 146, gap = 10, x0 = 480 + w / 2, h = Math.max(170, UI.tap(170)), y = 1080 - 18 - h / 2;
      return MOVES.map((m, i) => ({ move: m, x: x0 + i * (w + gap), y, w, h }));
    }
    drawCards(active) {
      const L = this.legal(), hintMove = this.hinted && this.phase === 'play' ? base(this.solution[0] || '') : '';
      this.cardRects().forEach((c, i) => {
        const lg = L.find(x => x.move === c.move) || { ok: false }, cfg = CARD[c.move];
        const hit = active ? UI._hit('card:' + c.move, c.x, c.y, c.w, c.h) : { hover: false, pressed: false, clicked: false };
        const lift = hit.pressed ? 3 : hit.hover ? -8 : 0, ok = lg.ok;
        g.save(); g.translate(c.x, c.y + lift); g.rotate([-0.02, 0.012, -0.008, 0.016, -0.014, 0.01, -0.012, 0.018, -0.01][i]);
        paperRect(0, 0, c.w, c.h, ok ? cfg.c : GREY, { seed: 800 + i, depth: hit.hover ? 1.8 : 1, alpha: ok ? 1 : 0.7, boil: false });
        const tc = ok ? (cfg.ink ? PAL.ink : PAL.cream) : 'rgba(43,36,32,0.45)';
        let fs = UI.text(40); g.font = `${fs}px 'Caveat Brush'`; const tw = g.measureText(label(c.move)).width; if (tw > c.w - 16) fs *= (c.w - 16) / tw;
        hand(label(c.move), 0, UI.touch() ? 0 : -14, fs, -1e6, { dur: 0.01, color: tc, seed: 810 + i });
        if (!UI.touch()) hand(cfg.sub, 0, 36, 21, -1e6, { dur: 0.01, color: tc, seed: 820 + i });
        if (UI.keys) { g.font = "24px 'Luckiest Guy'"; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = tc; g.fillText(String(i + 1), -c.w / 2 + 18, -c.h / 2 + 20); }
        if (!ok) { g.strokeStyle = 'rgba(43,36,32,0.35)'; g.lineWidth = 4; g.beginPath(); g.moveTo(-c.w / 2 + 14, c.h / 2 - 14); g.lineTo(c.w / 2 - 14, -c.h / 2 + 14); g.stroke(); }
        if (hintMove === c.move) { g.strokeStyle = PAL.red; g.lineWidth = 7; g.globalAlpha = 0.55 + 0.45 * Math.sin(T * 6); g.strokeRect(-c.w / 2 - 8, -c.h / 2 - 8, c.w + 16, c.h + 16); }
        g.restore();
        if (hit.clicked) { Audio.play('pop', { gain: 0.5 }); this.play(c.move); }
      });
    }
    drawReason() {
      const c = this.cardRects().find(x => x.move === this.reason.move) || this.cardRects()[4];
      const fs = UI.text(32), L = UI._wrap(this.reason.text, 560, `${fs}px 'Caveat Brush'`), h = L.length * fs * 1.15 + 36;
      const x = clamp(c.x, 520 + 300, 1880 - 300), y = c.y - c.h / 2 - h / 2 - 14, a = 1 - P(T, this.reason.at + 3.4, this.reason.at + 4);
      g.save(); g.globalAlpha *= a;
      paperRect(x, y, 620, h, PAL.white, { seed: 850, depth: 1.4, rot: -0.01, boil: false });
      L.forEach((l, i) => hand(l, x, y - (L.length - 1) * fs * 0.575 + i * fs * 1.15, fs, this.reason.at, { color: PAL.red, seed: 851 + i, dur: 0.25 }));
      g.restore();
    }
    drawGrain(docked) {
      const P_ = this.pending, ch = P_.choices;
      const txt = c => cap(c.to.kind) + (R().GRAIN && R().GRAIN[c.to.kind] ? ': ' + R().GRAIN[c.to.kind] : '');
      if (docked) {
        Dock.text('grain-q', [`${cap(this.state.kind)} breaks down into more than one kind of grain. Which one do you follow?`], { cls: 'note' });
        Dock.buttons('grain', ch.map((c, i) => ({ label: txt(c), color: ROCK[c.to.kind], ink: true, wide: true, onClick: () => this.grain(i) })).concat([{ label: 'Cancel', color: PAL.navy, onClick: () => this.cancelGrain() }]));
        return;
      }
      g.fillStyle = 'rgba(43,36,32,0.35)'; g.fillRect(0, 0, Game.W, Game.H);
      paperRect(960, 540, 1100, 520, PAL.cream, { seed: 900, depth: 2, boil: false });
      hand(`${cap(this.state.kind)} breaks down into more than one kind of grain.`, 960, 350, UI.text(44), P_.at, { seed: 901, dur: 0.4 });
      hand('Which one do you follow?', 960, 410, UI.text(44), P_.at + 0.2, { color: PAL.navy, seed: 902, dur: 0.3 });
      ch.forEach((c, i) => {
        const x = 960 + (i - (ch.length - 1) / 2) * 460;
        specimen(c.to, x, 540, 80, { sc: pop(T, P_.at + 0.2 + i * 0.1, 0.3) });
        if (R().GRAIN && R().GRAIN[c.to.kind]) hand(R().GRAIN[c.to.kind], x, 620 - 150, 30, P_.at + 0.4, { color: PAL.navy, seed: 903 + i, dur: 0.3 });
        if (UI.button('grain' + i, (UI.keys ? `${i + 1}. ` : '') + cap(c.to.kind), x, 680, { w: 320, h: 84, color: ROCK[c.to.kind], text: PAL.ink })) this.grain(i);
      });
      if (UI.button('grain-x', 'Cancel', 1400, 760, { w: 180, h: 64, color: PAL.navy })) this.cancelGrain();
    }

    /* portrait: the stage shows the rock and the column large; the cards and controls are dock buttons */
    drawBoardDocked() {
      const inst = this.mission, m = inst.item.mission, s = this.shown();
      const C = column(330, 150, 520, 920, true);
      this.drawMarker(C, 330, 80);
      this.drawRock(1150, 520, 250);
      hand(nameOf(s), 1150, 870, 110, -1e6, { dur: 0.01, color: PAL.ink, seed: 702, outline: PAL.cream });
      hand(s.place === 'surface' ? 'at the surface' : 'deep down', 1150, 975, 70, -1e6, { dur: 0.01, color: PAL.navy, seed: 703, outline: PAL.cream });
      paperRect(1700, 330, 360, 420, PAL.white, { seed: 740, depth: 1.2, rot: 0.02, boil: false });
      hand('GOAL', 1700, 175, 64, -1e6, { dur: 0.01, color: PAL.red, seed: 705 });
      specimen({ kind: m.to, place: 'surface', comp: 'granitic' }, 1700, 330, 120, { depth: 0.8 });
      hand(m.to, 1700, 480, 70, -1e6, { dur: 0.01, color: PAL.ink, seed: 706 });
      if (this.phase !== 'play' && this.phase !== 'grain') return;
      Dock.text('mission', [`Turn ${m.from} into ${m.to}.`], { cls: 'note' });
      Dock.text('clock', [`Moves ${this.moves.length} · par ${this.par}. Time so far: ${this.moves.length ? this.clockLabel() : 'none yet'}.`], { cls: 'small' });
      if (this.moves.length) Dock.text('trail', ['Your route: ' + this.moves.map(label).join(', ')], { cls: 'small' });
      if (this.phase === 'grain') return;
      const L = this.legal(), hm = this.hinted ? base(this.solution[0] || '') : '';
      Dock.buttons('cards', MOVES.map(mv => { const lg = L.find(x => x.move === mv), ok = lg && lg.ok;
        return { label: (hm === mv ? '→ ' : '') + label(mv), color: ok ? CARD[mv].c : GREY, ink: ok ? CARD[mv].ink : true, onClick: () => this.play(mv) }; }));
      if (this.reason && T - this.reason.at < 6) Dock.text('reason' + Math.round(this.reason.at * 10), ['✗ ' + label(this.reason.move) + ': ' + this.reason.text], { cls: 'note', reveal: true });
      if (this.canHint || this.hinted) {
        if (this.hinted) Dock.text('hinted', ['Hint used: no move stars this mission. Follow the arrow.'], { cls: 'small' });
        else Dock.buttons('hint', [{ label: 'Stuck? Show me', color: PAL.mustard, ink: true, onClick: () => this.hint() }]);
      }
      Dock.button({ id: 'undo', glyph: '⟲', label: 'undo', aria: 'undo the last move', onClick: () => this.undo() });
      Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'restart this mission', onClick: () => this.restart() });
    }

    /* the check question after reaching the goal */
    drawCheck(docked) {
      const q = this.mission, right = q.answerIndex, id = this.attempt + '_' + this.mi, answered = this.phase === 'answered';
      const res = answered ? this.results[this.mi] : null, tb = this.textbook();
      const wrongWhy = answered && this.pick !== right ? q.why[this.pick] : '';
      const tbLines = tb ? [`Textbook order: ${tb.textbook == null ? 'it never gets there' : tb.textbook + ' moves'}. You: ${tb.you} moves.`,
        tb.textbook == null || tb.you < tb.textbook ? 'Rocks don\'t have to go round the loop in order.' : `The fastest route takes ${tb.par} moves.`] : null;
      const starLine = res ? `Stars: ${res.stars} of 3 (${res.moveStars} for ${res.moves} moves, par ${res.par}${res.hinted ? ', hint used' : ''}; ${res.check ? '1' : '0'} for the question)` : '';
      if (docked) {
        Dock.text('reached' + id, [`Reached ${this.goal} in ${this.moves.length} moves (par ${this.par}).`], { cls: 'note' });
        const st = q.options.map((o, i) => (!answered ? '' : i === right ? 'right' : i === this.pick ? 'wrong' : 'faded'));
        Dock.choices('check' + id, { prompt: q.text, options: q.options, state: st, locked: answered, onChoose: i => this.answer(i) });
        if (answered) {
          Dock.text('verdict' + id, [this.pick === right ? 'Right!' : 'Not quite.'], { cls: this.pick === right ? 'right' : 'wrong' });
          Dock.text('explain' + id, wrongWhy ? [wrongWhy, q.explain] : [q.explain]);
          if (tbLines) Dock.text('textbook' + id, tbLines, { cls: 'note' });
          Dock.text('stars' + id, [starLine], { cls: 'small', reveal: true });
          Dock.button({ id: 'next', glyph: this.mi < this.n - 1 ? 'next mission ▶' : 'see my stars ▶', label: 'next', aria: 'next mission', go: true, onClick: () => this.next() });
        }
        return;
      }
      paperRect(960, 590, 1680, 880, PAL.cream, { seed: 950, depth: 2, boil: false });
      for (let y = 290; y < 1010; y += 58) ink([[140, y], [1780, y]], { w: 2, color: 'rgba(90,150,190,0.35)', seed: 951 + y, passes: 1, step: 80, amp: 0.8 });
      specimen(this.state, 250, 250, 60, { depth: 0.8 });
      hand(`Reached ${this.goal} in ${this.moves.length} moves (par ${this.par}). Now the check:`, 340, 250, 40, this.qAt, { align: 'left', color: PAL.navy, seed: 952, dur: 0.4 });
      const fq = UI.text(50), L = UI._wrap(q.text, 900, `${fq}px 'Caveat Brush'`);
      L.forEach((l, i) => hand(l, 200, 350 + i * fq * 1.15, fq, this.qAt + 0.2 + i * 0.25, { align: 'left', seed: 953 + i, dur: 0.45 }));
      const oh = UI.tap(92), top = 350 + L.length * fq * 1.15 + 20 + oh / 2;
      q.options.forEach((o, i) => {
        const y = top + i * (oh + 18);
        if (UI.button('opt' + id + '_' + i, `${i + 1}.  ${o}`, 660, y, { w: 920, h: 92, color: answered && i === right ? PAL.green : [PAL.mustard, PAL.sky, PAL.pink][i], text: PAL.ink, rot: [-0.006, 0.005, -0.004][i] })) this.answer(i);
        if (answered && (i === right || i === this.pick)) hand(i === right ? '✓' : '✗', 1180, y, 70, -1e6, { dur: 0.01, color: i === right ? PAL.green : PAL.red });
      });
      if (!answered) return;
      // right column: verdict, why, explanation, stars, the textbook card, next
      const fx = UI.text(32), X = 1230, W = 500;
      let y = 330;
      hand(this.pick === right ? 'Right!' : 'Not quite.', X, y, 56, this.pickedAt, { align: 'left', color: this.pick === right ? PAL.green : PAL.red, seed: 960, dur: 0.2 });
      y += 60;
      const body = (wrongWhy ? [[wrongWhy, PAL.red]] : []).concat([[q.explain, PAL.navy]]);
      body.forEach(([t, c], j) => UI._wrap(t, W, `${fx}px 'Caveat Brush'`).forEach(l => { hand(l, X, y, fx, this.pickedAt + 0.1 * j, { align: 'left', color: c, seed: 961 + Math.round(y), dur: 0.3 }); y += fx * 1.12; }));
      y += 26;
      for (let k = 0; k < 3; k++) doodleStar(X + 34 + k * 74, y + 10, 30, { color: k < res.stars ? PAL.mustard : '#d6ccbb', seed: 970 + k, s: pop(T, this.pickedAt + 0.3 + k * 0.12, 0.3) });
      hand(`${res.stars} of 3`, X + 250, y + 12, 40, this.pickedAt + 0.5, { align: 'left', color: PAL.ink, seed: 974, dur: 0.2 });
      y += 60;
      if (tbLines) {
        const h = 150, cy = Math.min(y + h / 2, 1000 - 60 - h / 2);
        paperRect(X + W / 2 - 10, cy, W + 20, h, PAL.white, { seed: 980, depth: 1.4, rot: -0.015, boil: false });
        tape(X + W / 2 - 10, cy - h / 2, 110, 0.04, undefined, 981);
        tbLines.forEach((l, i) => UI._wrap(l, W - 20, `${i ? 28 : 34}px 'Caveat Brush'`).slice(0, 2).forEach((w, j) => hand(w, X, cy - 40 + i * 50 + j * 30, i ? 28 : 34, this.pickedAt + 0.6, { align: 'left', color: i ? PAL.navy : PAL.red, seed: 982 + i * 3 + j, dur: 0.3 })));
      }
      if (UI.button('next', this.mi < this.n - 1 ? 'next mission ▶' : 'see my stars ▶', 1560, 975, { w: 340, h: 84, color: PAL.navy })) this.next();
    }

    drawIntro(docked) {
      const t0 = this.enteredAt, n = this.n, pass = this.passMissions;
      const lines = ['You are a grain of rock.', `${n} missions: turn one rock into another.`, 'Play the cards. Fewest moves = most stars.', `Reach ${pass} of the ${n} rocks for the stamp.`];
      specimen(R().state('granite', 'surface'), 420, 560, 170, { sc: pop(T, t0 + 0.3, 0.4) });
      specimen(R().state('magma', 'deep', 'granitic'), 330, 820, 90, { sc: pop(T, t0 + 0.5, 0.4) });
      specimen(R().state('sandstone', 'surface'), 560, 830, 80, { sc: pop(T, t0 + 0.6, 0.4) });
      if (docked) {
        Dock.heading('rocks-intro', "Rock's Long Trip", { cls: 'big' });
        Dock.text('rocks-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start the missions', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      paperRect(1180, 560, 1080, 560, PAL.cream, { seed: 990, depth: 1.6, rot: 0.008, boil: false });
      lines.forEach((l, i) => hand(l, 1180, 380 + i * 90, UI.text(i ? 50 : 60), t0 + 0.3 + i * 0.25, { color: i ? PAL.navy : PAL.ink, seed: 991 + i }));
      if (this.t > 0.8 && UI.button('start', 'Start ▶', 1180, 790, { w: 420, h: 110, color: PAL.red })) this.begin();
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons();
      const rows = this.instances.map((inst, i) => { const r = this.results[i] || { stars: 0, moves: 0, par: 0 }; const m = inst.item.mission; return { m, r, text: `${m.from} to ${m.to}: ${r.stars} of 3 stars (${r.moves} moves, par ${r.par})` }; });
      if (docked) {
        ransom(`${s} / ${of}`, 960, 420, 220, this.endAt + 0.1, { seed: 99 });
        UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 960, 760, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.8, color: this.passed ? PAL.green : PAL.red });
        Dock.heading('rocks-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('rocks-end', [this.passed ? 'Passed! Collect your stamp.' : `Not yet: reach ${this.passMissions} of the ${this.n} rocks for the stamp. New missions?`]);
        Dock.list('rocks-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 3 ? PAL.green : PAL.mustard, lines: [`${x.m.from} to ${x.m.to}`, `${x.r.stars} of 3 stars (${x.r.moves} moves, par ${x.r.par})`] })));
        Dock.text('rocks-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('rocks-end', btns.map((b, i) => ({ label: b.label, color: b.color, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      paperRect(960, 580, 1500, 860, PAL.cream, { seed: 995, depth: 1.6, boil: false });
      ransom(`${s} / ${of}`, 700, 290, 130, this.endAt + 0.1, { seed: 99 });
      UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 1330, 290, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.1, color: this.passed ? PAL.green : PAL.red });
      rows.forEach((x, i) => {
        const y = 430 + i * 74;
        for (let k = 0; k < 3; k++) doodleStar(330 + k * 50, y, 22, { color: k < x.r.stars ? PAL.mustard : '#d6ccbb', seed: 1000 + i * 3 + k });
        hand(x.text, 460, y, UI.text(38), this.endAt + 0.3 + i * 0.1, { align: 'left', color: PAL.ink, seed: 1020 + i, dur: 0.3 });
      });
      hand(this.passed ? 'Passed!' : `Reach ${this.passMissions} of the ${this.n} rocks for the stamp.`, 960, 820, UI.text(40), this.endAt + 0.8, { color: PAL.navy, seed: 1030 });
      hand(`quiz code ${this.seed}`, 960, 978, UI.text(30), this.endAt + 0.6, { color: PAL.navy, dur: 0.3 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('end' + i, b.label, 700 + i * 520, 895, { w: 460, h: 96, color: b.color })) b.go(); });
    }
  }
  // [sfx, gain, rate, dur?]: the lesson's own sounds (assets/sfx: crack, grind and lava are loops/long, so they are cut short)
  const SFX = { weather: ['crack', 0.45, 0.9], deposit: ['paper', 0.5, 0.8], cement: ['thunk', 0.6, 0.9], squeeze: ['grind', 0.45, 1, 1.2], melt: ['lava', 0.45, 1, 1.6],
    cool: ['sparkle', 0.35, 0.8], uplift: ['whoosh', 0.45, 1.3], bury: ['paper', 0.45, 0.7], erupt: ['lava', 0.5, 1.25, 1.2] };
  Rocks.STEP = STEP; Rocks.pool = pool; Rocks.MOVES = MOVES;
  /* the card rects of the live scene (tests click them) */
  Rocks.cards = () => { const s = Game.current(); return s instanceof Rocks ? s.cardRects() : []; };
  Levels.rocks = Rocks;
})();
